import React, { useState } from "react";
import "./filters.css";
import { Card, Col, Form, Alert, Spinner, Button, Row } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { IoMdClose } from "react-icons/io";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { BiAnalyse } from "react-icons/bi";
import { industriesNew } from "../../dropdown-options/industries";
import { countries } from "../../dropdown-options/countries";
import { entityTypes } from "../../dropdown-options/entity-types";
import { reasonsForShutdown } from "../../dropdown-options/reasons-for-shutdown";
import { lifespans } from "../../dropdown-options/lifespans";
import { fundingRounds } from "../../dropdown-options/funding-rounds";
import { fundingAmounts } from "../../dropdown-options/funding-amounts";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * GenderStatistics Filter Component
 * 
 * Analyzes founder gender distribution across startups.
 * Shows breakdown of founders by gender (Male, Female, Corporate) and founder composition patterns.
 * Filters by entity type, shutdown reasons, lifespan, location, industry, and funding data.
 * 
 * @param {Object} props - Component props including Supabase client
 * @param {Object} props.supabase - Supabase client instance
 * @returns {JSX.Element} Gender distribution analysis chart
 */
const GenderStatistics = (props) => {
  // State to control alert visibility
  const [alert, setAlert] = useState(false);

  // State to store alert message content
  const [message, setMessage] = useState("");

  // Close alert and clear message
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // Get current user and Supabase client from context
  const { currentUser, supabase } = useSupabaseApp();

  // Check if user is authenticated; redirect if not
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // State to manage filter form inputs
  const [filters, setFilters] = useState({
    type: "Gender",
    entityType: "Entity type",
    reasons: "Reasons for shutdown",
    lifespan: "Lifespan",
    location: "Location",
    industry: "Industry",
    fundingRound: "Funding round",
    fundingAmount: "Funding amount",
  });

  // hook for results
  const [investments, setInvestments] = useState([
    {
      Gender: "Male",
      Frequency: 1620,
      "%": 88.33,
    },
    {
      Gender: "Female",
      Frequency: 181,
      "%": 9.87,
    },
    {
      Gender: "Corporations",
      Frequency: 33,
      "%": 1.8,
    },
  ]);

  // Reset all filter form inputs to their default values and close any alert
  function resetFilters(e) {
    e.preventDefault();
    setFilters({
      type: "Gender",
      entityType: "Entity type",
      reasons: "Reasons for shutdown",
      lifespan: "Lifespan",
      location: "Location",
      industry: "Industry",
      fundingRound: "Funding round",
      fundingAmount: "Funding amount",
    });
    setAlert(false);
  }

  // Update filter state whenever user changes a form input value
  function changeFilters(e) {
    e.preventDefault();

    let name = e.target.name;
    let value = e.target.value;

    // Create a copy of current filters object
    let filtersCopy = { ...filters };

    // Update the changed filter property
    filtersCopy[name] = value;
    setFilters({ ...filtersCopy });
  }

  // Submit filters to Supabase edge function and fetch gender distribution data
  function submitFilters(e) {
    // Show loading alert while fetching data
    setAlert(true);
    setMessage(
      <div style={{ display: "flex", paddingTop: "10px" }}>
        <div style={{ width: "90%" }}>
          <p style={{ color: "#0f9d58" }}>
            <BiAnalyse className="alert-icon" /> Loading
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            Won't be long now. We're always doing our best to reduce waiting
            time.
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            If your waiting time is too long,{" "}
            <a
              href="https://www.roiquant.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>
            .
          </p>
        </div>
        <div style={{ width: "10%", textAlign: "right" }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </div>
    );

    // Call postmortemAnalysis edge function with current filter selections
    supabase.functions
      .invoke("postmortemAnalysis", { body: filters })
      .then((response) => {
        // Update chart with returned gender distribution data
        let graphData = response.data;
        setInvestments(graphData);
        closeAlert();
      })
      .catch((error) => {
        // Show error alert if query returns no results
        setAlert(true);
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px" }}>
              Your filter query returned no results. Please change your filter
              values and try again.
            </p>
          </div>
        );
      });
    e.preventDefault();
  }

  return (
    <div className="accordion-card-body">
      {/* loading alert */}

      {alert ? (
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button className="pitchdeckmessageclose" onClick={closeAlert}>
                <IoMdClose />
              </Button>
            </div>
            {message}
          </div>
        </Alert>
      ) : (
        ""
      )}

      <div className="col-12 div-filter">
        {/* form */}
        <Form onSubmit={submitFilters}>
          <Form.Group className="inputgroup">
            <Row className="align-items-center">
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.entityType}
                  name="entityType"
                  onChange={changeFilters}
                >
                  <option>Entity type</option>
                  {entityTypes.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.reasons}
                  name="reasons"
                  onChange={changeFilters}
                >
                  <option>Reasons for shutdown</option>
                  {reasonsForShutdown.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.lifespan}
                  name="lifespan"
                  onChange={changeFilters}
                >
                  <option>Lifespan</option>
                  {lifespans.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.location}
                  name="location"
                  onChange={changeFilters}
                >
                  <option>Location</option>
                  {countries.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.industry}
                  name="industry"
                  onChange={changeFilters}
                >
                  <option>Industry</option>
                  {industriesNew.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.fundingRound}
                  name="fundingRound"
                  onChange={changeFilters}
                >
                  <option>Funding round</option>
                  {fundingRounds.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.fundingAmount}
                  name="fundingAmount"
                  onChange={changeFilters}
                >
                  <option>Funding amount</option>
                  {fundingAmounts.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              {/* reset button */}
              <Col xs="auto">
                <button
                  onClick={resetFilters}
                  className="reset-button tour-clear-filter"
                >
                  <span>Clear</span>
                </button>
              </Col>
              {/* submit button */}
              <Col xs="auto">
                <button type="submit" className="submit-button">
                  <span>Filter</span>
                </button>
              </Col>
            </Row>
          </Form.Group>
        </Form>
      </div>

      {/* results */}
      <div className="col-12">
        <Card className="graph-card">
          <div>
            {/* chart component */}
            <ResponsiveContainer id="vc-chartsxs">
              <BarChart
                data={investments}
                margin={{ top: 20, right: 20, left: 20, bottom: 100 }}
                width={1100}
                height={500}
                fontFamily="AileronRegular"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="Gender"
                  interval={0}
                  fontSize="15px"
                  fontFamily="AileronRegular"
                  label={{
                    value: "Genders",
                    offset: -50,
                    position: "insideBottom",
                  }}
                />
                <YAxis
                  fontSize="15px"
                  fontFamily="AileronRegular"
                  label={{
                    value: "Frequency",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip />
                <Bar dataKey="Frequency" fill="#8884d8" barSize={50}>
                  <LabelList dataKey="%" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* notes */}
      <div className="col-12 extra-notes">
        <span className="info-notes">
          Note:
          <ul>
            <li id="notes-points">
              Based on{" "}
              {props.companiesWithGenderData
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
              inactive companies with recorded{" "}
              <span style={{ fontFamily: "AileronItalic" }}>
                founders' gender data
              </span>
              .
            </li>
            <li id="notes-points">
              In due time, the remaining{" "}
              {props.companiesWithGenderData
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
              inactive companies without founders' gender data will be
              identified and recorded by our algorithms.
            </li>
          </ul>
        </span>
      </div>
    </div>
  );
};

export default GenderStatistics;
